import { nanoid } from 'nanoid';
import {
  ApiResponse,
  Authorization,
  GetTokensRequestData,
  HelloWorldResponse,
  JsonWebToken,
  Tokens,
  UserAccountsSessionData,
  UserConsent,
  UserConsentUpdated
} from '../types/types';
import { getCodeChallenge } from './crypto';
import {
  LOGIN_REDIRECT,
  NALOG_API,
  NALOG_CLIENT,
  PUBLIC_API
} from '../constants/config';
import { callNalogAPI, callPublicAPI } from './api';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import { ACCOUNT } from '../constants/cookies';
import { addAttemptedViewToLocalStorage } from './util';

export const persistAuthorizationDataToLocalStorage = (
  data: Authorization
): void => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.GTM_USER_ID, data.gtm_user_id);
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.EXTERNAL_CONSENT,
    data.external_consent?.toString()
  );
  localStorage.setItem(LOCAL_STORAGE_KEYS.LEGAL_TYPE, data.legal_type);
  localStorage.setItem(LOCAL_STORAGE_KEYS.TYPE, data.type);
  localStorage.setItem(LOCAL_STORAGE_KEYS.TP_NAME, data.tp_name);
  localStorage.setItem(LOCAL_STORAGE_KEYS.PAYMENT_TYPE, data.payment_type);
};

export const persistGtmIdToLocalStorage = (gmtId: string) => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.GTM_USER_ID, gmtId);
};

export const persistUserAccountSessionAuthorizationDataToLocalStorage = (
  data: UserAccountsSessionData
): void => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.EXTERNAL_CONSENT,
    data.externalConsent.toString()
  );
  localStorage.setItem(LOCAL_STORAGE_KEYS.LEGAL_TYPE, data.legalType);
  localStorage.setItem(LOCAL_STORAGE_KEYS.TYPE, 'undefined'); // TODO: dodati u API odgovor ukoliko bude potrebe.
  localStorage.setItem(LOCAL_STORAGE_KEYS.TP_NAME, data.tpName);
  localStorage.setItem(LOCAL_STORAGE_KEYS.PAYMENT_TYPE, data.paymentType);
};

export const removeUserDataFromLocalStorage = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.GTM_USER_ID);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.EXTERNAL_CONSENT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.LEGAL_TYPE);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TYPE);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.TP_NAME);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.PAYMENT_TYPE);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.AVATAR_URL);

  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_CONSENT_UID);
  localStorage.removeItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_PHONE_CONTACT_CONSENTED
  );
  localStorage.removeItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_EMAIL_CONTACT_CONSENTED
  );
  localStorage.removeItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_DIRECT_MARKETING_CONSENTED
  );
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_CONSENT_TERMS_ACCEPTED);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_CONSENT_PARTNER_REDIRECTION);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_CONSENT_UPDATE_COUNT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_CONSENT_CONSENT_SHOWN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.IS_ADVANTAGE);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.COMBO_TYPE);

  localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.SHOULD_SHOW_QUESTIONNAIRE_MODAL);

  localStorage.removeItem(LOCAL_STORAGE_KEYS.ATTEMPTED_VIEW);
};

export const isUserLoggedIn = (): boolean => {
  const isDevelopment = import.meta.env.PROD !== true;
  const localLogInForce = import.meta.env.VITE_LOGGED_IN_LOCAL;

  if (
    isDevelopment &&
    (localLogInForce === 'true' || localLogInForce === 'false')
  ) {
    return localLogInForce === 'true';
  }

  const cookies = document.cookie;
  return cookies.indexOf(ACCOUNT) > -1;
};

export const userHasNalogCookie = () => {
  const cookies = document.cookie;
  const cookieList = cookies.split(';');
  const nalogCookie = cookieList.filter(
    (cookie: string) => cookie.indexOf(ACCOUNT) > -1
  );

  return Boolean(nalogCookie.length);
};

export const getUserNalogCookieValue = (): string => {
  const cookies = document.cookie;
  const cookieList = cookies.split(';');
  const nalogCookie = cookieList.filter(
    (cookie: string) => cookie.indexOf(ACCOUNT) > -1
  );
  return nalogCookie.length ? nalogCookie[0].split('=')[1] : '';
};

export const isUserAdvantageEnabled = () => {
  const isAdvantage = localStorage.getItem(LOCAL_STORAGE_KEYS.IS_ADVANTAGE);
  return Boolean(isAdvantage === '1');
};

export const generateState = () => {
  const state = localStorage.getItem(LOCAL_STORAGE_KEYS.STATE) || nanoid();
  localStorage.setItem(LOCAL_STORAGE_KEYS.STATE, state);
};

export function parseJsonWebToken(token: string): JsonWebToken {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {
      iss: '',
      aud: '',
      jti: '',
      nbf: 0,
      exp: 0,
      iat: 0
    };
  }
}

export const loginRequest = (): void => {
  const state = localStorage.getItem(LOCAL_STORAGE_KEYS.STATE) || '';
  const client_id = NALOG_CLIENT;
  const redirect_uri = LOGIN_REDIRECT;
  const scope = 'resource_info user_info';

  getCodeChallenge().then(code_challenge => {
    const params = new URLSearchParams();
    params.append('response_type', 'code');
    params.append('client_id', client_id);
    params.append('redirect_uri', redirect_uri);
    params.append('state', state);
    params.append('scope', scope);
    params.append('code_challenge', code_challenge);
    params.append('code_challenge_method', 'S256');

    const url = `${NALOG_API}/auth?${params.toString()}`;
    window.location.assign(url);
  });
};

export const logIn = (): void => {
  addAttemptedViewToLocalStorage();

  generateState();
  loginRequest();
};

export const authorizeWithAccessAndRefreshTokens = async (
  access_token: string,
  refresh_token: string
): Promise<ApiResponse<Authorization>> => {
  const data = {
    access_token,
    refresh_token
  };

  return await callPublicAPI<Authorization>('POST', '/auth', null, data);
};

export const getTokensFromCodeAndCodeVerifier = async (
  code: string,
  code_verifier: string
): Promise<Tokens> => {
  const data: GetTokensRequestData = {
    client_id: NALOG_CLIENT,
    grant_type: 'authorization_code',
    redirect_uri: LOGIN_REDIRECT,
    code,
    code_verifier
  };

  return callNalogAPI<Tokens>('POST', '/token', data, {
    onError: ({ message, status }) => {
      console.warn('callNalogAPI /token:', status, message);
    }
  });
};

export const getUserConsents = async (): Promise<ApiResponse<UserConsent>> => {
  return callPublicAPI<UserConsent>('GET', '/user-consents');
};

export const persistUserConsentsInLocalStorage = (
  consents: UserConsent
): void => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER_CONSENT_UID, consents.uid);
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_PHONE_CONTACT_CONSENTED,
    consents.phone_contact_consented?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_EMAIL_CONTACT_CONSENTED,
    consents.email_contact_consented?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_DIRECT_MARKETING_CONSENTED,
    consents.direct_marketing_consented?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_TERMS_ACCEPTED,
    consents.terms_and_conditions?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_PARTNER_REDIRECTION,
    consents.partner_redirection?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.IS_ADVANTAGE,
    consents.is_advantage?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.COMBO_TYPE,
    consents.combo_type?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_UPDATE_COUNT,
    consents.update_count?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_CONSENT_SHOWN,
    consents.consent_shown
  );
};

export const persistUserConsentMutationInLocalStorage = (
  consents: UserConsentUpdated
): void => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_CONSENT_SHOWN,
    consents.consent_shown
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_DIRECT_MARKETING_CONSENTED,
    consents.direct_marketing_consented?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_PARTNER_REDIRECTION,
    consents.partner_redirection?.toString() || ''
  );
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_UPDATE_COUNT,
    consents.update_count?.toString() || ''
  );
};

export const persistPhoneContactConsentInLocalStorage = (
  consent: 0 | 1
): void => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_PHONE_CONTACT_CONSENTED,
    consent.toString()
  );
};

export const persistEmailContactConsentInLocalStorage = (
  consent: 0 | 1
): void => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_EMAIL_CONTACT_CONSENTED,
    consent.toString()
  );
};

export const persistTermsAndConditionsAcceptedInLocalStorage = (
  consent: 0 | 1
): void => {
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_TERMS_ACCEPTED,
    consent.toString()
  );
};

export const shouldRedirectToPrivacySettings = (): boolean => {
  const isLoggedIn = isUserLoggedIn();
  if (!isLoggedIn) return false;

  const termsAndConditionsAccepted = localStorage.getItem(
    LOCAL_STORAGE_KEYS.USER_CONSENT_TERMS_ACCEPTED
  );
  if (termsAndConditionsAccepted !== '1') return true;

  const consentShown = new Date(
    localStorage.getItem(LOCAL_STORAGE_KEYS.USER_CONSENT_CONSENT_SHOWN) || ''
  );
  const marketingConsentGiven =
    localStorage.getItem(
      LOCAL_STORAGE_KEYS.USER_CONSENT_DIRECT_MARKETING_CONSENTED
    ) === '1';
  const differenceInDays = Math.floor(
    (new Date().getTime() - consentShown.getTime()) / (1000 * 3600 * 24)
  );
  return differenceInDays > 13 && !marketingConsentGiven;
};

export const mockLogin = (): void => {
  fetch(`${PUBLIC_API}/mock/auth`, {
    method: 'POST'
  })
    .then(res => res.json())
    .then((res: ApiResponse<Authorization>) => {
      persistAuthorizationDataToLocalStorage(res.data);
    })
    .finally(() => {
      const redirectToPath =
        localStorage.getItem(LOCAL_STORAGE_KEYS.ATTEMPTED_VIEW) || '/';
      window.location.assign(redirectToPath);
    });
};

export const getConsentDataForUser = async () => {
  return callPublicAPI<UserConsent>('GET', '/user-consents');
};

export const getUserDataFromTokens = async () => {
  const accessTokenFromLocalStorage =
    localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN) || '';
  const refreshTokenFromLocalStorage =
    localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN) || '';

  const data = {
    access_token: accessTokenFromLocalStorage,
    refresh_token: refreshTokenFromLocalStorage
  };

  return callPublicAPI<Authorization>('POST', '/auth', null, data);
};

export const getAndStoreUserDataByTokens = async () => {
  await getUserDataFromTokens().then(response =>
    persistAuthorizationDataToLocalStorage(response.data)
  );
};

export const getAndStoreUserDataBySession = async () => {
  await getUserDataFromSessionEndpoint().then(async response => {
    persistGtmIdToLocalStorage(response.session?.gtmUserId || '');
    persistUserAccountSessionAuthorizationDataToLocalStorage(response.data);
  });
};

export const getUserDataFromSessionEndpoint = async () => {
  return callPublicAPI<UserAccountsSessionData>(
    'GET',
    '/user-accounts/session'
  );
};

export const pingHelloWorld = async () => {
  return callPublicAPI<HelloWorldResponse>('GET', '/hello/world');
};
